/**
 * Workflow / State Transition Service
 * Pure functions for managing linear workflow transitions and state validation.
 */

import { FLOW_STEPS } from '../config/uiConfig';

/**
 * Standard progression order for items moving through delivery pipeline
 */
export const WORKFLOW_SEQUENCE: string[] = [
  'Tareas por hacer',
  'Planificación',
  'IN DEV',
  'En curso',
  'Pull request',
  'Ready for QA',
  'In QA',
  'Done',
];

/**
 * Returns the next logical workflow state in the pipeline, or null if terminal/unrecognized.
 */
export function getNextWorkflowState(currentState?: string): string | null {
  if (!currentState) return WORKFLOW_SEQUENCE[0];

  const normalized = currentState.trim().toLowerCase();

  // Special handling for On Hold
  if (normalized === 'on hold') {
    return 'IN DEV';
  }

  const currentIndex = WORKFLOW_SEQUENCE.findIndex(
    (step) => step.toLowerCase() === normalized
  );

  if (currentIndex === -1) {
    return null;
  }

  if (currentIndex >= WORKFLOW_SEQUENCE.length - 1) {
    return null; // Terminal state ('Done')
  }

  return WORKFLOW_SEQUENCE[currentIndex + 1];
}

/**
 * Returns all valid transitions from the given state.
 */
export function getAvailableTransitions(currentState?: string): string[] {
  if (!currentState) {
    return [...FLOW_STEPS];
  }

  const normalized = currentState.trim().toLowerCase();

  // If currently On Hold, allow resuming to earlier dev stages
  if (normalized === 'on hold') {
    return ['IN DEV', 'En curso', 'Planificación', 'Tareas por hacer'];
  }

  // If Done, allow reopening
  if (normalized === 'done') {
    return ['Tareas por hacer', 'IN DEV', 'Ready for QA'];
  }

  const currentIndex = WORKFLOW_SEQUENCE.findIndex(
    (step) => step.toLowerCase() === normalized
  );

  if (currentIndex === -1) {
    // Custom state: return standard sequence + On Hold
    return [...WORKFLOW_SEQUENCE, 'On Hold'];
  }

  const transitions = new Set<string>();

  // 1. Forward transition
  if (currentIndex < WORKFLOW_SEQUENCE.length - 1) {
    transitions.add(WORKFLOW_SEQUENCE[currentIndex + 1]);
  }

  // 2. Backward transition (rollback/re-planning)
  if (currentIndex > 0) {
    transitions.add(WORKFLOW_SEQUENCE[currentIndex - 1]);
  }

  // 3. Fast-forward to Done if in QA or Ready for QA
  if (currentIndex >= 5) {
    transitions.add('Done');
  }

  // 4. On Hold option for any active state
  transitions.add('On Hold');

  return Array.from(transitions);
}

export function isTerminalState(state?: string): boolean {
  if (!state) return false;
  return state.trim().toLowerCase() === 'done';
}

export const FlowService = {
  getNextWorkflowState,
  getAvailableTransitions,
  isTerminalState,
};

